// Wave 56 · Soundbox floating widget — full per-category mute controls.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Volume2, VolumeX, Repeat, ChevronDown, ChevronUp, Settings, X } from 'lucide-react';
import api from '../../utils/api';
import {
  getPrefs, setPrefs, listVoices, listLanguages,
  repeatLast, announce, getHistory, replayText,
  getMutedCategories, setMutedCategories,
  unlockSoundbox, isSoundboxUnlocked,
} from '../../utils/soundbox';

const SPEED_OPTIONS = [
  { key: 0.7, label: '🐢 Slow' },
  { key: 1.0, label: '🚶 Medium' },
  { key: 1.5, label: '🚀 Fast' },
];

// All alert categories with human labels
const CATEGORY_LABELS = {
  payment_received:    '💰 Payment Received',
  payment_sent:        '💸 Payment Sent / P2P',
  new_registration:    '👤 New Registration',
  new_order:           '🛒 New Order',
  order_update:        '📦 Order Status Update',
  order_confirmed:     '✅ Order Confirmed',
  order_shipped:       '🚚 Order Shipped',
  order_delivered:     '🎁 Order Delivered',
  order_rejected:      '❌ Order Rejected',
  withdraw_request:    '🏦 Withdraw Request',
  withdraw_paid:       '✅ Withdraw Paid',
  withdraw_rejected:   '❌ Withdraw Rejected',
  plan_purchase:       '⭐ Plan / Tool Purchase',
  new_auction_bid:     '🔨 New Auction Bid',
  at_deposit_request:  '📥 AT Deposit Request',
  at_deposit_approved: '✅ AT Deposit Approved',
  at_deposit_rejected: '❌ AT Deposit Rejected',
  new_b2c_demand:      '🏪 New B2C Demand (ATC)',
  new_b2b_demand:      '🏭 New B2B Demand (ATC)',
  new_bid_received:    '💬 New Bid (ATC)',
  deal_locked:         '🔒 Deal Locked (ATC)',
  escrow_confirmed:    '💳 Escrow Released (ATC)',
};

export default function SoundboxWidget({ compact = false }) {
  const [open, setOpen]           = useState(false);
  const [tab, setTab]             = useState('main'); // 'main' | 'mute'
  const [prefs, setLocalPrefs]    = useState(getPrefs());
  const [voices, setVoices]       = useState(listVoices());
  const [history, setHistory]     = useState(getHistory());
  const [mutedCats, setMutedCats] = useState(getMutedCategories());
  const seededRef                  = useRef(false);

  // Seed from admin defaults (only if user has no local override)
  useEffect(() => {
    if (seededRef.current) return;
    seededRef.current = true;
    try { if (localStorage.getItem('soundbox_prefs_v1')) return; } catch (_) {}
    api.get('/soundbox/defaults').then((r) => {
      const d = r.data || {};
      const next = setPrefs({ mute: !!d.mute_default, rate: d.default_rate || 1.0, lang: d.default_language || 'hi-IN', voice_name: d.default_voice_name || null });
      setLocalPrefs(next);
      // Apply admin-set per-category mutes as defaults (do NOT override if user already set)
      if (d.muted_categories && Object.keys(d.muted_categories).length > 0) {
        try {
          if (!localStorage.getItem('soundbox_muted_cats_v1')) {
            const newMap = setMutedCategories(d.muted_categories);
            setMutedCats(newMap);
          }
        } catch (_) {}
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const onVoices = () => setVoices(listVoices());
    window.addEventListener('soundbox:voices-loaded', onVoices);
    const t = setInterval(onVoices, 1500);
    setTimeout(() => clearInterval(t), 5000);
    return () => window.removeEventListener('soundbox:voices-loaded', onVoices);
  }, []);

  useEffect(() => {
    const onPrefs = (e) => setLocalPrefs(e.detail || getPrefs());
    window.addEventListener('soundbox:prefs', onPrefs);
    return () => window.removeEventListener('soundbox:prefs', onPrefs);
  }, []);

  useEffect(() => {
    const onHist = () => setHistory(getHistory());
    window.addEventListener('soundbox:history', onHist);
    window.addEventListener('storage', onHist);
    return () => { window.removeEventListener('soundbox:history', onHist); window.removeEventListener('storage', onHist); };
  }, []);

  useEffect(() => {
    const onCats = (e) => setMutedCats(e.detail || getMutedCategories());
    window.addEventListener('soundbox:muted-cats', onCats);
    return () => window.removeEventListener('soundbox:muted-cats', onCats);
  }, []);

  const languages    = useMemo(() => listLanguages(), [voices.length]);
  const voicesInLang = useMemo(() => voices.filter((v) => v.lang === prefs.lang), [voices, prefs.lang]);

  const update     = (patch) => setLocalPrefs(setPrefs(patch));
  const toggleMute = () => update({ mute: !prefs.mute });

  const toggleCat = (cat) => {
    const next = { ...mutedCats, [cat]: !mutedCats[cat] };
    setMutedCats(setMutedCategories(next));
  };

  const mutedCount = Object.values(mutedCats).filter(Boolean).length;

  return (
    <div
      data-testid="soundbox-widget"
      className={`${compact ? 'inline-flex' : 'fixed bottom-20 right-3 md:top-4 md:bottom-auto md:right-4'} z-[90] flex flex-col-reverse md:flex-col items-end`}
    >
      {/* Pill */}
      <div className="flex items-center gap-1.5">
        <button onClick={repeatLast} data-testid="soundbox-quick-repeat-btn" title="Repeat last alert"
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-full font-black text-[11px] shadow-lg bg-slate-900/90 text-emerald-300 border border-emerald-400/50">
          <Repeat size={12} /> Repeat
        </button>
        <button onClick={() => setOpen((v) => !v)} data-testid="soundbox-toggle"
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full font-black text-[11px] shadow-lg backdrop-blur transition ${
                  prefs.mute ? 'bg-slate-800/90 text-slate-400 border border-slate-700'
                             : 'bg-gradient-to-r from-emerald-500 to-cyan-500 text-slate-900 border border-emerald-300'
                }`}>
          {prefs.mute ? <VolumeX size={13} /> : <Volume2 size={13} />}
          <span>Soundbox</span>
          {mutedCount > 0 && !prefs.mute && <span className="bg-amber-400 text-slate-900 px-1 rounded-full text-[8px]">{mutedCount} muted</span>}
          {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        </button>
      </div>

      {/* Expanded panel */}
      {open && (
        <div data-testid="soundbox-panel"
             className="mb-2 md:mb-0 md:mt-2 w-80 max-w-[calc(100vw-1.5rem)] bg-slate-950/97 backdrop-blur-md border border-emerald-400/40 rounded-2xl p-3 shadow-2xl space-y-2.5 text-white">
          
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="text-sm font-black flex items-center gap-1.5">🔊 Soundbox <span className="text-[9px] opacity-50 font-normal">0 cost</span></div>
            <div className="flex items-center gap-1">
              <button onClick={() => setTab(tab === 'mute' ? 'main' : 'mute')} data-testid="soundbox-mute-settings-btn" title="Per-alert mute settings"
                      className={`px-2 py-1 rounded-lg text-[10px] font-black flex items-center gap-1 ${tab === 'mute' ? 'bg-amber-500 text-slate-900' : 'bg-slate-800 text-amber-300'}`}>
                <Settings size={10} /> Alert Mutes {mutedCount > 0 && `(${mutedCount})`}
              </button>
              <button onClick={toggleMute} data-testid="soundbox-mute-btn"
                      className={`px-2 py-1 rounded-lg text-[10px] font-black flex items-center gap-1 ${prefs.mute ? 'bg-rose-500 text-white' : 'bg-emerald-500 text-slate-900'}`}>
                {prefs.mute ? <><VolumeX size={11} /> Muted</> : <><Volume2 size={11} /> Live</>}
              </button>
            </div>
          </div>

          {/* Tab: Per-category mute */}
          {tab === 'mute' && (
            <div className="space-y-1.5">
              <div className="text-[10px] text-amber-300 font-black">Alert Type Mute/Unmute (toggle to silence specific events)</div>
              <div className="max-h-64 overflow-y-auto space-y-1 pr-1">
                {Object.entries(CATEGORY_LABELS).map(([cat, label]) => {
                  const isMuted = !!mutedCats[cat];
                  return (
                    <button key={cat} onClick={() => toggleCat(cat)}
                            data-testid={`soundbox-cat-${cat}`}
                            className={`w-full flex items-center justify-between px-2 py-1.5 rounded-lg text-[10px] font-black transition ${
                              isMuted ? 'bg-rose-500/20 text-rose-300 border border-rose-400/30' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                            }`}>
                      <span>{label}</span>
                      <span className={`px-1.5 py-0.5 rounded text-[8px] ${isMuted ? 'bg-rose-500 text-white' : 'bg-emerald-500 text-slate-900'}`}>
                        {isMuted ? '🔇 OFF' : '🔊 ON'}
                      </span>
                    </button>
                  );
                })}
              </div>
              <button onClick={() => { setMutedCats(setMutedCategories({})); }} className="w-full py-1 rounded-lg bg-slate-700 text-slate-300 text-[10px] font-black">
                Reset All (Unmute All)
              </button>
            </div>
          )}

          {/* Tab: Main settings */}
          {tab === 'main' && (
            <>
              {/* Language */}
              <div>
                <div className="text-[10px] opacity-70 mb-0.5">Language</div>
                <select value={prefs.lang} onChange={(e) => update({ lang: e.target.value, voice_name: null })}
                        data-testid="soundbox-lang-select"
                        className="w-full px-2 py-1.5 rounded-lg bg-slate-900 border border-slate-700 text-[11px]">
                  {languages.map((l) => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>

              {/* Voice */}
              <div>
                <div className="text-[10px] opacity-70 mb-0.5">Voice ({voicesInLang.length} available)</div>
                <select value={prefs.voice_name || ''} onChange={(e) => update({ voice_name: e.target.value || null })}
                        data-testid="soundbox-voice-select"
                        className="w-full px-2 py-1.5 rounded-lg bg-slate-900 border border-slate-700 text-[11px]">
                  <option value="">Auto (first {prefs.lang} voice)</option>
                  {voicesInLang.map((v) => <option key={v.name} value={v.name}>{v.name}</option>)}
                </select>
              </div>

              {/* Speed */}
              <div>
                <div className="text-[10px] opacity-70 mb-0.5">Speed</div>
                <div className="grid grid-cols-3 gap-1">
                  {SPEED_OPTIONS.map((s) => (
                    <button key={s.key} onClick={() => update({ rate: s.key })} data-testid={`soundbox-speed-${s.key}`}
                            className={`px-2 py-1.5 rounded-lg text-[10px] font-black transition ${Math.abs(prefs.rate - s.key) < 0.05 ? 'bg-cyan-500 text-slate-900' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'}`}>
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Test / Repeat */}
              <div className="grid grid-cols-2 gap-1.5">
                <button onClick={repeatLast} data-testid="soundbox-repeat-btn"
                        className="px-2 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-[11px] font-black flex items-center justify-center gap-1">
                  <Repeat size={11} /> Repeat Last
                </button>
                <button onClick={() => { unlockSoundbox(); setTimeout(() => announce('payment_received', { amount: 500 }), 300); }} data-testid="soundbox-test-btn"
                        className="px-2 py-1.5 rounded-lg bg-fuchsia-500 hover:bg-fuchsia-400 text-slate-900 text-[11px] font-black">
                  🔔 Test Voice
                </button>
              </div>

              {/* Last 10 Alerts */}
              <div data-testid="soundbox-history">
                <div className="text-[10px] opacity-70 mb-1">🕘 Last 10 Alerts</div>
                {history.length === 0 ? (
                  <div className="text-[10px] opacity-50">No alerts yet — koi transaction hone do</div>
                ) : (
                  <div className="max-h-44 overflow-y-auto space-y-1 pr-1">
                    {history.map((h, i) => (
                      <button key={`${h.at}-${i}`} onClick={() => replayText(h.text)} data-testid={`soundbox-history-${i}`}
                              className="w-full text-left px-2 py-1 rounded-lg bg-slate-900 hover:bg-slate-800 border border-slate-800 text-[10px] flex items-start gap-1.5">
                        <Repeat size={10} className="mt-0.5 shrink-0 text-emerald-300" />
                        <span className="flex-1 leading-snug">{h.text}</span>
                        <span className="opacity-50 shrink-0">{new Date(h.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="text-[9px] opacity-50 leading-snug">Payments, orders, bids, registrations, withdrawals — sab kuch bolega browser mein. Zero server cost.</div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
